import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ProductsFilterParams,
  ProductType,
  ProductsListResponse,
  CreateProductParams,
  UpdateProductParams
} from '@/types/product'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'

// API functions
import { fetchProducts, fetchProduct, createProduct, updateProduct, deleteProduct, bulkDeleteProducts } from '@/lib/api'

type ProductStateType = {
  data: ProductType | null
  isLoading: boolean
  error: string | null
}

type ProductsListStateType = {
  data: ProductsListResponse | null
  isLoading: boolean
  error: string | null
}

type ProductsStoreState = {
  product: ProductStateType
  productsList: ProductsListStateType
  filters: ProductsFilterParams
  selectedProductIds: string[]
  setFilters: (filters: Partial<ProductsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchProducts: (params?: ProductsFilterParams, currency?: string) => Promise<ProductsListResponse>
  resetProductsList: () => void

  // Product operations
  getProduct: (id: string, currency?: string) => Promise<ProductType>
  createProduct: (params: CreateProductParams) => Promise<ProductType>
  updateProduct: (id: string, params: UpdateProductParams) => Promise<ProductType>
  deleteProduct: (id: string) => Promise<void>
  bulkDeleteProducts: (ids: string[]) => Promise<BulkDeleteResponse>

  // Selection operations
  selectProduct: (id: string) => void
  deselectProduct: (id: string) => void
  toggleProductSelection: (id: string) => void
  selectAllProducts: (products: ProductType[]) => void
  deselectAllProducts: () => void
  isProductSelected: (id: string) => boolean
  resetProduct: () => void
}

const defaultFilters: ProductsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const useProductsStore = create<ProductsStoreState>()(
  devtools((set, get) => ({
    product: {
      data: null,
      isLoading: false,
      error: null
    },
    productsList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedProductIds: [],

    filters: defaultFilters,

    setFilters: filters => {
      set(state => ({
        filters: { ...state.filters, ...filters }
      }))
    },

    resetFilters: () => {
      set({ filters: defaultFilters })
    },

    updateUrlParams: searchParams => {
      const filters: Partial<ProductsFilterParams> = {}

      for (const [key, value] of searchParams.entries()) {
        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as ProductsFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof ProductsFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getProduct: async (id, currency?) => {
      set(state => ({
        product: {
          ...state.product,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProduct(id, currency)

        set(state => ({
          product: {
            ...state.product,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          product: {
            ...state.product,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createProduct: async params => {
      set(state => ({
        product: {
          ...state.product,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createProduct(params)

        set(state => ({
          product: {
            ...state.product,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          product: {
            ...state.product,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updateProduct: async (id, params) => {
      set(state => ({
        product: {
          ...state.product,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updateProduct(id, params)

        set(state => ({
          product: {
            ...state.product,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          product: {
            ...state.product,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deleteProduct: async id => {
      set(state => ({
        product: {
          ...state.product,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deleteProduct(id)

        set(state => ({
          product: {
            ...state.product,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          product: {
            ...state.product,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProduct: () => {
      set(state => ({
        product: {
          ...state.product,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectProduct: id => {
      set(state => {
        if (state.selectedProductIds.includes(id)) {
          return state
        }
        return { selectedProductIds: [...state.selectedProductIds, id] }
      })
    },

    deselectProduct: id => {
      set(state => ({
        selectedProductIds: state.selectedProductIds.filter(productId => productId !== id)
      }))
    },

    toggleProductSelection: id => {
      set(state => {
        const isSelected = state.selectedProductIds.includes(id)
        if (isSelected) {
          return { selectedProductIds: state.selectedProductIds.filter(productId => productId !== id) }
        } else {
          return { selectedProductIds: [...state.selectedProductIds, id] }
        }
      })
    },

    selectAllProducts: products => {
      set({ selectedProductIds: products.map(product => product.id) })
    },

    deselectAllProducts: () => {
      set({ selectedProductIds: [] })
    },

    isProductSelected: id => {
      return get().selectedProductIds.includes(id)
    },

    bulkDeleteProducts: async ids => {
      set(state => ({
        product: {
          ...state.product,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteProducts({ ids })

        set(state => ({
          product: {
            ...state.product,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedProductIds: state.selectedProductIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          product: {
            ...state.product,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchProducts: async (params?: ProductsFilterParams, currency?: string) => {
      const filtersToUse = params || get().filters

      set(state => ({
        productsList: {
          ...state.productsList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchProducts(filtersToUse, currency)

        set(state => ({
          productsList: {
            ...state.productsList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          productsList: {
            ...state.productsList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetProductsList: () => {
      set(state => ({
        productsList: {
          ...state.productsList,
          data: null,
          error: null
        }
      }))
    }
  }))
)

export default useProductsStore
